import React, { useState, useEffect } from 'react';
import { Settings, Save, AlertCircle, DollarSign, Zap, Gauge } from 'lucide-react';
import apiService from '../../../services/api';

interface TierConfiguration {
  id: number;
  tier: number;
  name: string;
  description: string;
  model: string;
  maxTokens: number;
  temperature: number;
  inputCostPerMillion: number;
  outputCostPerMillion: number;
  dailyLimit: number | null;
  monthlyLimit: number | null;
  baseResponseFormat?: string | null;
  responseFormatVariables?: string[];
  responseExample?: Record<string, any> | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const AVAILABLE_MODELS = [
  'google/gemini-2.5-flash',
  'google/gemini-2.5-pro',
  'anthropic/claude-sonnet-4',
  'anthropic/claude-3.5-haiku',
  'openai/gpt-4o',
  'openai/gpt-4o-mini',
];

// Model cost mapping from testing service
const MODEL_COSTS: Record<string, { input: number; output: number }> = {
  'anthropic/claude-3.5-haiku': { input: 1, output: 5 },
  'anthropic/claude-sonnet-4': { input: 3, output: 15 },
  'google/gemini-2.5-pro': { input: 1.25, output: 10 },
  'google/gemini-2.5-flash': { input: 0.3, output: 2.5 },
  'openai/gpt-4o': { input: 2.5, output: 10 },
  'openai/gpt-4o-mini': { input: 0.15, output: 0.6 },
};

export default function TierConfigurationManager() {
  const [configurations, setConfigurations] = useState<TierConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [editingConfig, setEditingConfig] = useState<Partial<TierConfiguration> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTierConfigurations();
  }, []);

  const fetchTierConfigurations = async () => {
    try {
      setError(null);
      const response = await apiService.get('/api/admin/prompts/tier-configurations');
      setConfigurations(response.data);
    } catch (error) {
      console.error('Failed to fetch tier configurations:', error);
      setError('Failed to load tier configurations. Please check your authentication and try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateTierConfiguration = async (tier: number, updates: Partial<TierConfiguration>) => {
    setSaving(prev => ({ ...prev, [tier]: true }));
    
    try {
      await apiService.put(`/api/admin/prompts/tier-configurations/${tier}`, updates);
      await fetchTierConfigurations();
      setEditingConfig(null);
    } catch (error) {
      console.error('Failed to update tier configuration:', error);
    } finally {
      setSaving(prev => ({ ...prev, [tier]: false }));
    }
  };

  const handleEdit = (config: TierConfiguration) => {
    setEditingConfig({ ...config });
  };

  const handleSave = async () => {
    if (!editingConfig || editingConfig.tier === undefined) return;
    
    const { tier, ...updates } = editingConfig;
    await updateTierConfiguration(tier, updates);
  };

  const getTierIcon = (tier: number) => {
    switch (tier) {
      case 1:
        return <Zap className="w-5 h-5 text-green-500" />;
      case 2:
        return <Gauge className="w-5 h-5 text-blue-500" />;
      case 3:
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Settings className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTierBadgeColor = (tier: number) => {
    switch (tier) {
      case 1:
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 2:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 3:
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const getModelCost = (model: string): { input: number; output: number } => {
    return MODEL_COSTS[model] || { input: 3, output: 15 }; // Default to Sonnet pricing
  };

  const handleModelChange = (model: string) => {
    if (!editingConfig) return;
    
    const cost = getModelCost(model);
    setEditingConfig(prev => prev ? {
      ...prev,
      model,
      inputCostPerMillion: cost.input,
      outputCostPerMillion: cost.output
    } : null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <AlertCircle className="w-5 h-5" />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            AI Tier Configurations
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Configure AI models, token limits, and costs for each analysis tier
          </p>
        </div>
      </div>

      {/* Help text for dynamic variables */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">Dynamic Variables Support</h4>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          The base response format supports dynamic variables that are replaced based on your threat categories:
        </p>
        <ul className="mt-2 space-y-1 text-sm text-blue-700 dark:text-blue-300">
          <li>• <code className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800 rounded text-xs">{`{{categoryScoreFields}}`}</code> - Generates category score fields (e.g., "MILITARY": 0-10)</li>
          <li>• <code className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800 rounded text-xs">{`{{reasoningByCategoryFields}}`}</code> - Generates reasoning fields for each category</li>
        </ul>
      </div>

      <div className="grid gap-6">
        {configurations.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No tier configurations found. Please check your database setup.</p>
          </div>
        ) : (
          configurations.map((config) => (
          <div
            key={config.tier}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getTierIcon(config.tier)}
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                      {config.name}
                    </h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTierBadgeColor(config.tier)}`}>
                      Tier {config.tier}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {config.description}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleEdit(config)}
                className="px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Edit
              </button>
            </div>

            {editingConfig && editingConfig.tier === config.tier ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Model
                  </label>
                  <select
                    value={editingConfig.model || ''}
                    onChange={(e) => handleModelChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {AVAILABLE_MODELS.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    value={editingConfig.maxTokens || 0}
                    onChange={(e) => setEditingConfig(prev => prev ? { ...prev, maxTokens: parseInt(e.target.value) } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Temperature
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={editingConfig.temperature || 0}
                    onChange={(e) => setEditingConfig(prev => prev ? { ...prev, temperature: parseFloat(e.target.value) } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Input Cost ($/1M tokens)
                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                      Auto-updated from model
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingConfig.inputCostPerMillion || 0}
                    onChange={(e) => setEditingConfig(prev => prev ? { ...prev, inputCostPerMillion: parseFloat(e.target.value) } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Output Cost ($/1M tokens)
                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                      Auto-updated from model
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingConfig.outputCostPerMillion || 0}
                    onChange={(e) => setEditingConfig(prev => prev ? { ...prev, outputCostPerMillion: parseFloat(e.target.value) } : null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Daily Limit
                  </label>
                  <input
                    type="number"
                    value={editingConfig.dailyLimit || ''}
                    onChange={(e) => setEditingConfig(prev => prev ? { ...prev, dailyLimit: e.target.value ? parseInt(e.target.value) : null } : null)}
                    placeholder="No limit"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Base Response Format
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      (JSON schema - supports {`{{categoryScoreFields}}`} and {`{{reasoningByCategoryFields}}`})
                    </span>
                  </label>
                  <textarea
                    value={editingConfig.baseResponseFormat || ''}
                    onChange={(e) => setEditingConfig(prev => prev ? { ...prev, baseResponseFormat: e.target.value || null } : null)}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                    placeholder="Enter JSON response format instructions..."
                  />
                </div>
                
                <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t">
                  <button
                    onClick={() => setEditingConfig(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving[config.tier]}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    {saving[config.tier] ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Model</div>
                  <div className="text-sm text-gray-900 dark:text-white font-mono">
                    {config.model}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Max Tokens</div>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {config.maxTokens.toLocaleString()}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Temperature</div>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {config.temperature}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    Input Cost
                  </div>
                  <div className="text-sm text-gray-900 dark:text-white">
                    ${config.inputCostPerMillion}/1M tokens
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    Output Cost
                  </div>
                  <div className="text-sm text-gray-900 dark:text-white">
                    ${config.outputCostPerMillion}/1M tokens
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily Limit</div>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {config.dailyLimit ? config.dailyLimit.toLocaleString() : 'No limit'}
                  </div>
                </div>
                
                {config.baseResponseFormat && (
                  <div className="md:col-span-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Base Response Format</div>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                      <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words font-mono">
                        {config.baseResponseFormat.substring(0, 300)}
                        {config.baseResponseFormat.length > 300 ? '...' : ''}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))
        )}
      </div>
    </div>
  );
}